import React, { useState, useEffect } from "react";
import apiClient from "../../utils/apiClient";
import CoursesGrid from "./CoursesGrid";
import { Alert } from "react-bootstrap";
import Shimmer from "../Common/Shimmer";
import { useNavigate } from "react-router-dom";
import CommonToaster from "../Common/CommonToaster";
import OverLimitBanner from "../Common/OverLimitBanner";
import { ConfirmModal, AlertModal, AppButton, Paginator, FilterPanel, FilterField, NativeSelect, FilterButton, ActiveFilterChips, PageHeader } from "../ui";
import { usePlanUsage, isOverLimit, isStrictlyOverLimit } from "../../hooks/usePlanUsage";
import { BookOpen, Plus, Search, Tags } from "lucide-react";
import CourseCategoriesModal from "./CourseCategoriesModal";
import { fetchCourseCatalogCategories } from "../../utils/courseCatalogApi";
import { useBranchKey } from "../../contexts/BranchContext";
import { hasPermission } from "../../utils/permissions";
import { getCurrentUser } from "../../utils/auth";
import "./Courses.scss";

const DEFAULT_PAGE_SIZE = 12;

const LEVEL_OPTIONS = [
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
];

const CoursesPage = () => {
  const [filters, setFilters] = useState({ search: "", category: "", level: "" });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [view, setView] = useState("grid");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '' });
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();
  const canEditCourses = hasPermission("add_edit_courses");
  // Deleting a course is the owner's alone on the server.
  const canDeleteCourses = getCurrentUser()?.usertype === "superadmin";

  // Branch scope is part of this list's identity: switching branch changes
  // which courses the API returns, so it belongs in the fetch deps. The
  // context's `vc-branch-change` event had no listeners, which is why pages
  // that did not depend on the selection kept showing the previous branch.
  const branchKey = useBranchKey();

  // Plan usage limits
  const { usage } = usePlanUsage();
  const courseLimit = isOverLimit(usage?.courses);
  const courseStrictly = isStrictlyOverLimit(usage?.courses);

  useEffect(() => {
    const fetchCoursesAndOptions = async () => {
      setLoading(true);
      setErr("");
      try {
        const params = { courseType: 'batch', page, pageSize };
        if (filters.search) params.search = filters.search;
        if (filters.category) params.category = filters.category;
        if (filters.level) params.level = filters.level;

        const { data } = await apiClient.get("/courses/list?q=info", { params });
        setCourses(data.courses || []);
        setTotal(data.total || 0);
      } catch (error) {
        setErr("Failed to load courses. Please try again.");
        setToast({ show: true, message: "Failed to load courses.", type: "error" });
      } finally {
        setLoading(false);
      }
    };
    fetchCoursesAndOptions();
  }, [filters, page, pageSize, branchKey]);

  // Reset to the first page whenever the filters change
  useEffect(() => {
    setPage(1);
  }, [filters]);

  // Catalog categories for the filter dropdown (id → label).
  useEffect(() => {
    fetchCourseCatalogCategories()
      .then((rows) => setCategoryOptions(Array.isArray(rows) ? rows : []))
      .catch(() => setCategoryOptions([]));
  }, []);

  const activeFilterCount = (filters.category ? 1 : 0) + (filters.level ? 1 : 0);
  const handleResetFilters = () => setFilters((f) => ({ ...f, category: "", level: "" }));

  // Removable chips for applied filters (search stays inline).
  const clearField = (field) => setFilters((f) => ({ ...f, [field]: "" }));
  const activeFilters = [
    filters.category && { key: "category", label: `Category: ${categoryOptions.find((c) => c.id === filters.category)?.label || filters.category}`, onRemove: () => clearField("category") },
    filters.level && { key: "level", label: `Level: ${LEVEL_OPTIONS.find((l) => l.value === filters.level)?.label || filters.level}`, onRemove: () => clearField("level") },
  ].filter(Boolean);

  const handleDelete = (id) => setDeleteTarget(id);

  const confirmDelete = async () => {
    const id = deleteTarget;
    setDeleteTarget(null);
    try {
      await apiClient.delete(`/courses/delete/${id}`);
      setCourses(prev => prev.filter(c => c._id !== id));
      setTotal(prev => Math.max(0, prev - 1));
      setToast({ show: true, message: "Course deleted successfully!", type: "success" });
    } catch (error) {
      if (error.response?.data?.errorCode === 'COURSE_HAS_BATCHES') {
        setAlertModal({ show: true, title: 'Cannot Delete Course', message: error.response.data.message });
      } else {
        setToast({ show: true, message: "Failed to delete course.", type: "error" });
      }
    }
  };

  return (
    <div className="cr-page">
      <PageHeader
        icon={BookOpen}
        title="Courses"
        description="Manage batch-based courses and their content"
      />
      <OverLimitBanner
        resourceName="Courses"
        used={courseLimit.used}
        max={courseLimit.max}
        overLimit={courseLimit.overLimit}
        strictlyOver={courseStrictly.overLimit}
      />

      {/* Toolbar: Filters (left) — Actions (right) */}
      <div className="cr-toolbar">
        <div className="cr-toolbar-left">
          <div className="cr-search">
            <Search size={16} className="cr-search-icon" />
            <input
              type="text"
              className="cr-search-input"
              placeholder="Search courses..."
              value={filters.search}
              onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
            />
          </div>

          {/* Single filter trigger — opens the slide-over on all viewports */}
          <FilterButton count={activeFilterCount} onClick={() => setShowFilterModal(true)} />
        </div>

        <div className="cr-toolbar-right">
          <AppButton variant="outline" size="sm" onClick={() => setCategoriesOpen(true)}>
            <Tags size={16} /> Categories
          </AppButton>
          {canEditCourses && (
            <AppButton
              size="sm"
              onClick={() => navigate("/courses/add")}
              disabled={courseLimit.overLimit}
              title={courseLimit.overLimit ? `Plan limit reached (${courseLimit.used}/${courseLimit.max})` : ''}
            >
              <Plus size={16} /> Add Course
            </AppButton>
          )}
        </div>
      </div>

      {/* Active filter chips */}
      <ActiveFilterChips items={activeFilters} onClearAll={handleResetFilters} />

      {/* Mirrors CoursesGrid's own `row g-4` / `col-12 col-md-6 col-lg-4`, so the
          page does not reflow when the courses land. A spinner here was also
          invisible to the visual spec's busy-detector, which polls for
          `.spinner-border` and `[class*="animate-shimmer"]` — hence a baseline
          frozen mid-load. */}
      {loading && (
        <div className="row g-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="col-12 col-md-6 col-lg-4">
              <Shimmer height={240} />
            </div>
          ))}
        </div>
      )}
      {err && (
        <Alert variant="danger" className="my-3 text-center">
          {err}
        </Alert>
      )}
      {!loading && !err && (
        <>
          <CoursesGrid courses={courses} onDelete={canDeleteCourses ? handleDelete : undefined} canManage={canEditCourses} />
          <Paginator
            page={page}
            totalPages={Math.ceil(total / pageSize)}
            total={total}
            pageSize={pageSize}
            onChange={setPage}
            infoLabel="courses"
          />
        </>
      )}
      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete Course"
        message="Are you sure you want to delete this course?"
        confirmText="Delete"
        variant="danger"
      />

      <AlertModal
        show={alertModal.show}
        onClose={() => setAlertModal({ show: false, title: '', message: '' })}
        title={alertModal.title}
        message={alertModal.message}
        variant="danger"
      />

      <FilterPanel
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        subtitle="Filter courses by category and level."
        onReset={handleResetFilters}
        appliedCount={activeFilterCount}
      >
        <FilterField label="Category">
          <NativeSelect size="lg"
            value={filters.category}
            onChange={(e) => setFilters(f => ({ ...f, category: e.target.value }))}
          >
            <option value="">All Categories</option>
            {categoryOptions.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </NativeSelect>
        </FilterField>
        <FilterField label="Level">
          <NativeSelect size="lg"
            value={filters.level}
            onChange={(e) => setFilters(f => ({ ...f, level: e.target.value }))}
          >
            <option value="">All Levels</option>
            {LEVEL_OPTIONS.map((l) => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </NativeSelect>
        </FilterField>
      </FilterPanel>

      <CourseCategoriesModal show={categoriesOpen} onHide={() => setCategoriesOpen(false)} canManage={canEditCourses} />
    </div>
  );
};

export default CoursesPage;
